import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import useCardIndex from '@/hooks/useCardIndex';
import { auth } from '@/services/firebase';
import { addCardsBatchToList, addCardToList, removeCardFromList, subscribeToAllListCards, updateListCard } from '@/services/listCards';
import { createList, deleteList, subscribeToLists, updateList } from '@/services/lists';
import type { CardList, ListCard } from '@/types/lists';
import type { AddListCard, CreateList, UpdateList, UpdateListCard } from '@/types/schemas';

import { listCardsByListAtom, listCardsByOracleIdAtom, listCardsStateAtom, listedOracleIdsAtom, listsStateAtom } from '../state/listsAtom';

// ============================================================================
// Sync Hooks
// ============================================================================

/**
 * Sync lists from Firestore when authenticated.
 * Returns loading state.
 */
export function useListsSync(isAuthenticated: boolean): boolean {
  const [{ loading }, setState] = useAtom(listsStateAtom);

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser) {
      setState({ data: [], loading: false });
      return;
    }

    const unsubscribe = subscribeToLists(auth.currentUser.uid, (data) => {
      setState({ data, loading: false });
    });

    return unsubscribe;
  }, [isAuthenticated, setState]);

  return loading;
}

/**
 * Sync ALL list cards from Firestore when authenticated.
 * Returns loading state.
 */
export function useListCardsSync(isAuthenticated: boolean): boolean {
  const [{ loading }, setState] = useAtom(listCardsStateAtom);

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser) {
      setState({ data: [], loading: false });
      return;
    }

    const unsubscribe = subscribeToAllListCards(auth.currentUser.uid, (data) => {
      setState({ data, loading: false });
    });

    return unsubscribe;
  }, [isAuthenticated, setState]);

  return loading;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Get all lists for the current user.
 */
export function useLists(): { data: CardList[]; isLoading: boolean } {
  const { data, loading } = useAtomValue(listsStateAtom);
  return { data, isLoading: loading };
}

/**
 * Get all list cards for the current user.
 */
export function useAllListCards(): { data: ListCard[]; isLoading: boolean } {
  const { data, loading } = useAtomValue(listCardsStateAtom);
  return { data, isLoading: loading };
}

/**
 * Get cards for a specific list.
 */
export function useListCards(listId: string): ListCard[] {
  const cardsByList = useAtomValue(listCardsByListAtom);
  return cardsByList.get(listId) ?? [];
}

/**
 * Check if a card (by oracle_id) is on any list.
 */
export function useIsOnList(oracleId: string | undefined): boolean {
  const listedIds = useAtomValue(listedOracleIdsAtom);
  return oracleId ? listedIds.has(oracleId) : false;
}

/**
 * Get all list entries for a specific card (by oracle_id).
 */
export function useCardListEntries(oracleId: string | undefined): ListCard[] {
  const cardsByOracleId = useAtomValue(listCardsByOracleIdAtom);
  return oracleId ? (cardsByOracleId.get(oracleId) ?? []) : [];
}

/**
 * Get a specific list by ID.
 */
export function useList(listId: string): CardList | undefined {
  const { data: lists } = useLists();
  return lists?.find((l) => l.id === listId);
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new list.
 */
export function useCreateList() {
  return useCallback(async (data: CreateList): Promise<string> => {
    return createList(data);
  }, []);
}

/**
 * Hook to update a list.
 */
export function useUpdateList() {
  return useCallback(async (listId: string, data: UpdateList): Promise<void> => {
    return updateList(listId, data);
  }, []);
}

/**
 * Hook to delete a list and all its cards.
 */
export function useDeleteList() {
  return useCallback(async (listId: string): Promise<void> => {
    return deleteList(listId);
  }, []);
}

/**
 * Hook to add a card to a list with optimistic update.
 * Resolves scryfall_id from card index if not provided.
 * Deduplicates by list_id + scryfall_id when scryfall_id is available.
 */
export function useAddCardToList() {
  const [cardsState, setCardsState] = useAtom(listCardsStateAtom);
  const { getCardByOracleId } = useCardIndex();

  // Ref for stable callback — avoids re-creating on every list card change
  const allCardsRef = useRef(cardsState.data);
  allCardsRef.current = cardsState.data;

  return useCallback(
    async (data: AddListCard): Promise<string> => {
      // Resolve scryfall_id: use provided value, or look up default from card index
      const resolvedScryfallId = data.scryfall_id || getCardByOracleId(data.oracle_id)?.id || '';

      // Dedup: skip if an entry with the same list_id + scryfall_id already exists
      if (resolvedScryfallId) {
        const existing = allCardsRef.current.find((c) => c.list_id === data.list_id && c.scryfall_id === resolvedScryfallId);
        if (existing) {
          return existing.id;
        }
      }

      // Optimistic update
      const tempId = `temp-${Date.now()}`;
      const optimisticCard: ListCard = {
        id: tempId,
        list_id: data.list_id,
        oracle_id: data.oracle_id,
        name: data.name,
        quantity: data.quantity,
        priority: data.priority,
        notes: data.notes ?? null,
        added_at: new Date(),
        scryfall_id: resolvedScryfallId,
      };

      setCardsState((prev) => ({
        ...prev,
        data: [...prev.data, optimisticCard],
      }));

      try {
        const id = await addCardToList({ ...data, scryfall_id: resolvedScryfallId });
        // The real-time subscription will update the state with the correct ID
        return id;
      } catch (error) {
        // Revert on error
        setCardsState((prev) => ({
          ...prev,
          data: prev.data.filter((c) => c.id !== tempId),
        }));
        throw error;
      }
    },
    [setCardsState, getCardByOracleId],
  );
}

/**
 * Hook to update a list card with optimistic update.
 */
export function useUpdateListCard() {
  const [, setCardsState] = useAtom(listCardsStateAtom);

  return useCallback(
    async (cardId: string, data: UpdateListCard): Promise<void> => {
      // Store previous state for rollback
      let previousCard: ListCard | undefined;

      // Optimistic update
      setCardsState((prev) => {
        previousCard = prev.data.find((c) => c.id === cardId);

        // If quantity is 0, remove the card
        if (data.quantity !== undefined && data.quantity <= 0) {
          return {
            ...prev,
            data: prev.data.filter((c) => c.id !== cardId),
          };
        }

        return {
          ...prev,
          data: prev.data.map((c) => (c.id === cardId ? ({ ...c, ...data } as ListCard) : c)),
        };
      });

      try {
        await updateListCard(cardId, data);
      } catch (error) {
        // Revert on error
        if (previousCard) {
          setCardsState((prev) => ({
            ...prev,
            data:
              data.quantity !== undefined && data.quantity <= 0
                ? [...prev.data, previousCard!]
                : prev.data.map((c) => (c.id === cardId ? previousCard! : c)),
          }));
        }
        throw error;
      }
    },
    [setCardsState],
  );
}

/**
 * Hook to remove a card from a list with optimistic update.
 */
export function useRemoveCardFromList() {
  const [, setCardsState] = useAtom(listCardsStateAtom);

  return useCallback(
    async (cardId: string): Promise<void> => {
      // Store previous state for rollback
      let previousCard: ListCard | undefined;

      // Optimistic update
      setCardsState((prev) => {
        previousCard = prev.data.find((c) => c.id === cardId);
        return {
          ...prev,
          data: prev.data.filter((c) => c.id !== cardId),
        };
      });

      try {
        await removeCardFromList(cardId);
      } catch (error) {
        // Revert on error
        if (previousCard) {
          setCardsState((prev) => ({
            ...prev,
            data: [...prev.data, previousCard!],
          }));
        }
        throw error;
      }
    },
    [setCardsState],
  );
}

/**
 * Hook to add multiple cards in Firestore batches (500 per batch).
 * Deduplicates by list_id + scryfall_id when scryfall_id is available.
 * Calls `onProgress` after each batch commits.
 */
export function useAddListCardsBatch() {
  const { data: allCards } = useAtomValue(listCardsStateAtom);
  const allCardsRef = useRef(allCards);
  allCardsRef.current = allCards;

  return useCallback(async (cards: AddListCard[], onProgress?: (committed: number) => void): Promise<string[]> => {
    // Dedup: filter out cards where list_id + scryfall_id already exists
    const deduped = cards.filter((c) => {
      if (!c.scryfall_id) return true;
      return !allCardsRef.current.some((existing) => existing.list_id === c.list_id && existing.scryfall_id === c.scryfall_id);
    });
    if (deduped.length === 0) return [];
    return addCardsBatchToList(deduped, onProgress);
  }, []);
}
